import { UnreachableError } from 'base/helpers';
import { NestedNode, NestedTagNode } from './schema';

export type ReplaceableTextContent = {
  id: string;
  original: string;
};

export function findReplaceableTextContent(
  roots: NestedNode[],
): ReplaceableTextContent[] {
  const output: ReplaceableTextContent[] = [];
  function walk(node: NestedNode) {
    if (node.text) return;
    if (isReplaceableTextNode(node)) {
      output.push({
        id: node._id,
        original: textContentsOf(node),
      });
    } else {
      node.children.forEach(walk);
    }
  }
  roots.forEach(walk);
  return output;
}

export const enum TextReplacementType {
  CHILDREN = 'children',
  VALUE = 'value',
  PLACEHOLDER = 'placeholder',
  SELECT_FIRST_OPTION = 'select-first-option',
}

/**
 * Selects the node which we'll replace the text contents of.
 **/
export function isReplaceableTextNode(
  node: NestedTagNode,
): TextReplacementType | undefined {
  if (node.type === 'RichText') {
    return TextReplacementType.CHILDREN;
  }
  if (node.type === 'FormButton') {
    return TextReplacementType.VALUE;
  }
  if (node.type === 'FormTextInput' || node.type === 'FormTextarea') {
    return TextReplacementType.PLACEHOLDER;
  }
  if (
    node.type === 'FormSelect' &&
    node.data.form?.type === 'select' &&
    (node.data.form.opts?.length || 0) > 0
  ) {
    return TextReplacementType.SELECT_FIRST_OPTION;
  }
  if (
    node.type === 'Link' &&
    node.children.length === 1 &&
    node.children[0].text &&
    node.children[0].v.toLowerCase() === 'open modal'
  ) {
    // don't let people edit this button, as it is hidden under the modal in any modal component
    return undefined;
  }
  if (node.children.length > 0 && node.children.some((ch) => ch.text)) {
    // this covers many node types, e.g. <p> <h1> etc
    return TextReplacementType.CHILDREN;
  }
  return undefined;
}

/**
 * Gets the original text contents of a node.  Must be a replaceable text node.
 **/
export function textContentsOf(node: NestedNode, trim = true): string {
  if (node.text) return node.v;
  const type = isReplaceableTextNode(node);
  if (type === TextReplacementType.VALUE) {
    return node.data.attr?.value || '';
  } else if (type === TextReplacementType.PLACEHOLDER) {
    return node.data.attr?.placeholder || '';
  } else if (type === TextReplacementType.SELECT_FIRST_OPTION) {
    const { form } = node.data;
    if (form?.type !== 'select') return '';
    return form.opts?.[0]?.t || '';
  } else if (
    type === TextReplacementType.CHILDREN ||
    // special case - recursing into children
    type == null
  ) {
    const suffix = node.tag === 'p' ? '\n\n' : '';
    const value =
      node.children.flatMap((n) => textContentsOf(n, false)).join('') + suffix;
    return trim ? value.trim() : value;
  } else {
    throw new UnreachableError(type);
  }
}

function createParentByIdMap(roots: NestedNode[]): Map<string, NestedTagNode> {
  const out = new Map<string, NestedTagNode>();

  function visit(node: NestedNode) {
    if (node.text) return;
    for (const child of node.children) {
      out.set(child._id, node);
      visit(child);
    }
  }

  for (const root of roots) {
    visit(root);
  }
  return out;
}
